/**
 * MODAL: FILTRES AVANCÉS
 * Modal pour appliquer des filtres sur les colonnes du tableau
 */

import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { FilterCondition, FilterConfig, FilterMode } from '../../types/filters';
import { Input } from '../ui/input';
import { Modal } from '../ui/modal';
import { Select } from '../ui/select';

interface FiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  filterConfigs: FilterConfig[];
  activeFilters: FilterCondition[];
  activeFilterMode?: FilterMode;
  onApply: (filters: FilterCondition[], mode: FilterMode) => void;
}

const OPERATOR_LABELS: Record<string, string> = {
  equals: 'Est égal à',
  notEquals: 'Est différent de',
  contains: 'Contient',
  notContains: 'Ne contient pas',
  startsWith: 'Commence par',
  notStartsWith: 'Ne commence pas par',
  endsWith: 'Finit par',
  notEndsWith: 'Ne finit pas par',
  greaterThan: 'Est supérieur à',
  lessThan: 'Est inférieur à',
  before: 'Avant',
  after: 'Après',
  between: 'Entre',
  sameDay: 'Le jour du',
  isNotNull: 'Est défini',
  isNull: "N'est pas défini",
};

/**
 * Helper pour parser/créer la valeur between
 * Between attend un tableau de 2 éléments [min, max]
 */
function parseBetweenValue(value: string | string[]): [string, string] {
  if (Array.isArray(value) && value.length === 2) {
    return [value[0], value[1]];
  }
  return ['', ''];
}

function getFilterGroup(config: FilterConfig): string {
  if (config.group) return config.group;
  const field = config.field.toLowerCase();
  if (field.includes('date') || field.includes('_at') || field.includes('created')) return 'Dates';
  if (field.includes('payment') || field.includes('bpi') || field.includes('bed'))
    return 'Paiement';
  if (field.includes('product') || field.includes('form') || field.includes('millesime')) {
    return 'Produit et événement';
  }
  if (field.includes('status') || field.includes('state')) return 'Statut';
  if (field.includes('assigned') || field.includes('user')) return 'Affectation';
  if (
    field.includes('name') ||
    field.includes('email') ||
    field.includes('phone') ||
    field.includes('number')
  ) {
    return 'Identification';
  }
  return 'Autres';
}

export function FiltersModal({
  isOpen,
  onClose,
  filterConfigs,
  activeFilters,
  activeFilterMode = 'and',
  onApply,
}: FiltersModalProps) {
  const [filters, setFilters] = useState<FilterCondition[]>(activeFilters);
  const [filterMode, setFilterMode] = useState<FilterMode>(activeFilterMode);
  const [fieldSearch, setFieldSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setFilters(activeFilters);
    setFilterMode(activeFilterMode);
    setFieldSearch('');
  }, [activeFilterMode, activeFilters, isOpen]);

  const groupedConfigs = useMemo(() => {
    const normalizedSearch = fieldSearch.trim().toLocaleLowerCase('fr');
    const visibleConfigs = normalizedSearch
      ? filterConfigs.filter((config) =>
          config.label.toLocaleLowerCase('fr').includes(normalizedSearch)
        )
      : filterConfigs;

    return visibleConfigs.reduce<Map<string, FilterConfig[]>>((groups, config) => {
      const group = getFilterGroup(config);
      groups.set(group, [...(groups.get(group) || []), config]);
      return groups;
    }, new Map());
  }, [fieldSearch, filterConfigs]);

  if (!isOpen) return null;

  const handleAddFilter = () => {
    const firstConfig = Array.from(groupedConfigs.values()).flat()[0] || filterConfigs[0];
    if (!firstConfig) return;

    setFilters([
      ...filters,
      {
        field: firstConfig.field,
        operator: firstConfig.operators[0],
        value: '',
        label: firstConfig.label,
      },
    ]);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters(filters.filter((_, i) => i !== index));
  };

  const handleFilterChange = (
    index: number,
    key: keyof FilterCondition,
    value: string | string[]
  ) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], [key]: value };

    // Si on change de champ, réinitialiser l'opérateur et la valeur
    if (key === 'field') {
      const config = filterConfigs.find((c) => c.field === value);
      if (config) {
        newFilters[index].operator = config.operators[0];
        newFilters[index].value = '';
        newFilters[index].label = config.label;
      }
    }

    // Si on change d'opérateur, réinitialiser la valeur pour between
    if (key === 'operator') {
      const newOperator = value as string;
      const oldOperator = filters[index].operator;

      if (newOperator === 'between' && oldOperator !== 'between') {
        newFilters[index].value = ['', ''];
      } else if (newOperator !== 'between' && oldOperator === 'between') {
        newFilters[index].value = '';
      }
    }

    setFilters(newFilters);
  };

  const handleApply = () => {
    // Filtrer les filtres vides (sauf isNotNull/isNull qui n'ont pas besoin de valeur)
    const validFilters = filters.filter((f) => {
      if (f.operator === 'isNotNull' || f.operator === 'isNull') return true;
      if (f.operator === 'between') {
        const [min, max] = parseBetweenValue(f.value);
        return min !== '' && max !== '';
      }
      return f.value && f.value !== '';
    });
    onApply(validFilters, filterMode);
    onClose();
  };

  const handleReset = () => {
    setFilters([]);
    setFilterMode('and');
  };

  const getConfigForField = (field: string) => {
    return filterConfigs.find((c) => c.field === field);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Filtres"
      size="2xl"
      footer={
        <div className="flex items-center justify-between w-full">
          <button
            onClick={handleReset}
            className="text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            Réinitialiser
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-zinc-900 bg-white border border-zinc-200 rounded-md shadow-xs hover:bg-zinc-50 transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 text-sm font-medium text-white bg-zinc-900 rounded-md shadow-md hover:bg-zinc-800 transition-colors"
            >
              Appliquer
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        {filterConfigs.length > 8 && (
          <div className="space-y-2">
            <label htmlFor="filter-field-search" className="text-sm font-medium text-zinc-700">
              Rechercher un critère
            </label>
            <Input
              id="filter-field-search"
              type="search"
              value={fieldSearch}
              onChange={(event) => setFieldSearch(event.target.value)}
              placeholder="Nom, statut, produit, date…"
            />
          </div>
        )}

        {/* Sélecteur ET/OU - visible uniquement si plusieurs filtres */}
        {filters.length > 1 && (
          <div className="flex items-center gap-3 p-3 bg-zinc-100 rounded-lg">
            <span className="text-sm font-medium text-zinc-700">Combiner les filtres avec :</span>
            <div className="flex items-center gap-1 bg-white rounded-md border border-zinc-200 p-0.5">
              <button
                type="button"
                onClick={() => setFilterMode('and')}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                  filterMode === 'and'
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
              >
                ET
              </button>
              <button
                type="button"
                onClick={() => setFilterMode('or')}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                  filterMode === 'or'
                    ? 'bg-zinc-900 text-white'
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
              >
                OU
              </button>
            </div>
            <span className="text-xs text-zinc-500">
              {filterMode === 'and'
                ? '(Tous les critères doivent correspondre)'
                : '(Au moins un critère doit correspondre)'}
            </span>
          </div>
        )}

        {filters.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm text-zinc-500 mb-4">Aucun filtre appliqué</p>
            <button onClick={handleAddFilter} className="text-sm text-zinc-900 hover:underline">
              Ajouter un filtre
            </button>
          </div>
        ) : (
          filters.map((filter, index) => {
            const config = getConfigForField(filter.field);
            if (!config) return null;
            const fieldGroups = new Map(groupedConfigs);
            const currentFieldIsVisible = Array.from(fieldGroups.values()).some((configs) =>
              configs.some((candidate) => candidate.field === filter.field)
            );
            if (!currentFieldIsVisible) {
              const group = getFilterGroup(config);
              fieldGroups.set(group, [config, ...(fieldGroups.get(group) || [])]);
            }

            return (
              <div key={index} className="flex gap-2 items-start p-4 bg-zinc-50 rounded-md">
                {/* Sélection du champ */}
                <div className="flex-1">
                  <Select
                    value={filter.field}
                    onChange={(e) => handleFilterChange(index, 'field', e.target.value)}
                    className="w-full"
                  >
                    {Array.from(fieldGroups.entries()).map(([group, configs]) => (
                      <optgroup key={group} label={group}>
                        {configs.map((cfg) => (
                          <option key={cfg.field} value={cfg.field}>
                            {cfg.label}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                </div>

                {/* Sélection de l'opérateur */}
                <div className="flex-1">
                  <Select
                    value={filter.operator}
                    onChange={(e) => handleFilterChange(index, 'operator', e.target.value)}
                    className="w-full"
                  >
                    {config.operators.map((op) => (
                      <option key={op} value={op}>
                        {OPERATOR_LABELS[op]}
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Valeur (masquée pour isNotNull/isNull, double pour between) */}
                <div className="flex-1">
                  {filter.operator === 'isNotNull' || filter.operator === 'isNull' ? (
                    <div className="w-full h-9 px-3 text-sm bg-zinc-100 border border-zinc-200 rounded-md flex items-center text-zinc-500">
                      -
                    </div>
                  ) : filter.operator === 'between' ? (
                    // Double input pour between
                    <div className="flex gap-2">
                      <input
                        type={config.type === 'date' ? 'date' : 'number'}
                        value={parseBetweenValue(filter.value)[0]}
                        onChange={(e) => {
                          const [, max] = parseBetweenValue(filter.value);
                          handleFilterChange(index, 'value', [e.target.value, max]);
                        }}
                        placeholder="Min"
                        className="w-1/2 h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md shadow-xs focus:outline-none focus:ring-1 focus:ring-zinc-900"
                      />
                      <input
                        type={config.type === 'date' ? 'date' : 'number'}
                        value={parseBetweenValue(filter.value)[1]}
                        onChange={(e) => {
                          const [min] = parseBetweenValue(filter.value);
                          handleFilterChange(index, 'value', [min, e.target.value]);
                        }}
                        placeholder="Max"
                        className="w-1/2 h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md shadow-xs focus:outline-none focus:ring-1 focus:ring-zinc-900"
                      />
                    </div>
                  ) : config.type === 'date' ? (
                    <input
                      type="date"
                      value={filter.value as string}
                      onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                      className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md shadow-xs focus:outline-none focus:ring-1 focus:ring-zinc-900 pr-3 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-datetime-edit-fields-wrapper]:text-zinc-500 [&::-webkit-datetime-edit-text]:text-zinc-500 [&::-webkit-datetime-edit-month-field]:text-zinc-500 [&::-webkit-datetime-edit-day-field]:text-zinc-500 [&::-webkit-datetime-edit-year-field]:text-zinc-500"
                    />
                  ) : config.type === 'number' ? (
                    <input
                      type="number"
                      value={filter.value as string}
                      onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                      placeholder="Valeur..."
                      className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md shadow-xs focus:outline-none focus:ring-1 focus:ring-zinc-900"
                    />
                  ) : config.type === 'select' ? (
                    <Select
                      value={filter.value as string}
                      onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                      className="w-full"
                      placeholder="Sélectionner..."
                      options={config.options}
                    />
                  ) : (
                    <input
                      type="text"
                      value={filter.value as string}
                      onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                      placeholder="Valeur..."
                      className="w-full h-9 px-3 text-sm bg-white border border-zinc-200 rounded-md shadow-xs focus:outline-none focus:ring-1 focus:ring-zinc-900"
                    />
                  )}
                </div>

                {/* Bouton supprimer */}
                <button
                  onClick={() => handleRemoveFilter(index)}
                  className="flex items-center justify-center size-9 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  <X className="size-4" />
                </button>
              </div>
            );
          })
        )}

        {filters.length > 0 && (
          <button
            onClick={handleAddFilter}
            className="text-sm text-zinc-700 hover:text-zinc-900 transition-colors"
          >
            + Ajouter un filtre
          </button>
        )}
      </div>
    </Modal>
  );
}
